import React, { useState } from 'react';
import { useRovingTabIndex } from '../../hooks/useRovingTabIndex';

export interface ProductInfoTab {
  id: string;
  label: string;
  /** Rendered panel content; omitted/empty tabs still render a placeholder. */
  content: React.ReactNode;
}

interface ProductInfoTabsProps {
  tabs: ProductInfoTab[];
}

/**
 * Tabbed product-info strip for the enterprise PDP layout
 * (`branding.productDetail.infoLayout === "tabs"`, issue #143).
 *
 * Active tab is underlined with `border-b-2 border-secondary`. Keyboard
 * navigation reuses the shared {@link useRovingTabIndex} contract so it
 * matches IndustryTabs (Left/Right + Home/End, roving tabindex, focus
 * follows selection). Rendered only when a store opts into the tabs layout
 * via `branding.productDetail.infoLayout === "tabs"`; stores that keep the
 * stacked info never render this.
 */
const ProductInfoTabs: React.FC<ProductInfoTabsProps> = ({ tabs }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const { onKeyDown, getTabIndex } = useRovingTabIndex(activeIndex, tabs.length, setActiveIndex);

  if (!tabs || tabs.length === 0) return null;

  const safeIndex = Math.min(activeIndex, tabs.length - 1);
  const active = tabs[safeIndex];

  return (
    <div className="mt-16" data-testid="pdp-info-tabs">
      <div
        role="tablist"
        aria-label="Product information"
        className="flex gap-8 border-b border-border-light overflow-x-auto"
      >
        {tabs.map((tab, index) => {
          const isActive = index === safeIndex;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`pdp-tab-${tab.id}`}
              aria-selected={isActive}
              aria-controls={`pdp-panel-${tab.id}`}
              tabIndex={getTabIndex(index)}
              onClick={() => setActiveIndex(index)}
              onKeyDown={onKeyDown}
              className={`
                -mb-px whitespace-nowrap py-3 text-base font-semibold transition-colors
                focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary
                ${isActive
                  ? 'border-b-2 border-secondary text-text-primary'
                  : 'border-b-2 border-transparent text-text-secondary hover:text-text-primary'
                }
              `}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div
        role="tabpanel"
        id={`pdp-panel-${active.id}`}
        aria-labelledby={`pdp-tab-${active.id}`}
        // tabIndex=0 so keyboard users can focus + scroll the panel when its
        // content has no focusable children (WAI-ARIA tabs pattern).
        // max-w-prose caps prose line length (~65ch) for readability.
        tabIndex={0}
        className="py-8 max-w-prose text-text-secondary leading-relaxed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        {active.content}
      </div>
    </div>
  );
};

export default ProductInfoTabs;
